// 카테고리별 상품 목록 화면.
// 작업계획서 4.4 (17, 20): 쇼핑몰형 카드 그리드. 품절은 회색 + 라벨 + 장바구니 비활성 (ProductCard 가 처리).

import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { listCategories, listProducts } from "@/features/catalog/catalogApi.ts";
import ProductCard from "@/features/catalog/ProductCard.tsx";

const PAGE_SIZE = 20;

// 카테고리별 상품 목록 화면.
export default function CategoryProductsPage() {
  const { id = "" } = useParams<{ id: string }>();

  const cats = useQuery({ queryKey: ["catalog", "categories"], queryFn: listCategories });
  const categoryName = cats.data?.find((c) => c.id === id)?.name ?? "";

  const query = useInfiniteQuery({
    queryKey: ["catalog", "products", { categoryId: id }],
    queryFn: ({ pageParam }) =>
      listProducts({ categoryId: id, cursor: pageParam as string | undefined, limit: PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!id,
  });

  // 스크롤 바닥 근접 시 다음 페이지 자동 로드.
  useEffect(() => {
    function onScroll() {
      if (!query.hasNextPage || query.isFetchingNextPage) return;
      const remaining = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
      if (remaining < 400) {
        void query.fetchNextPage();
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [query]);

  const products = query.data?.pages.flatMap((p) => p.products) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return (
    <div className="flex flex-col">
      <header className="sticky top-[var(--safe-top)] z-10 flex items-center gap-2 border-b border-ink-200 bg-white px-4 py-3">
        <Link to="/category" className="rounded-md px-2 py-1 text-sm hover:bg-ink-100" aria-label="뒤로">
          ← 카테고리
        </Link>
        <h1 className="ml-auto text-lg font-bold">{categoryName || "카테고리"}</h1>
        <span className="ml-auto text-xs text-ink-600">{total}개</span>
      </header>
      <main className="mx-auto w-full max-w-screen-sm px-4 py-4">
        {query.isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded-lg bg-ink-100" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="rounded-md border border-dashed border-ink-300 p-8 text-center text-sm text-ink-600">
            이 카테고리에는 아직 상품이 없습니다.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
            {query.isFetchingNextPage ? (
              <p className="py-4 text-center text-sm text-ink-600">불러오는 중...</p>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
